/**
 * Consolidated geography input (issue #1926). Replaces the flat 53-row
 * Yes/Maybe/No list for the `geography` question with a compact editor:
 * bulk "all states" + per-region (US Census 4 regions) shortcuts, a
 * search-to-add box, and a consolidated chip summary split into the three
 * preference tiers — Sweet spot (Yes), Open to (Maybe), Excluded (No).
 *
 * Front-end only: it reads and writes the same `MultiselectAnswer`
 * (`stateCode → yes|maybe|no`) the old list did, one segment's worth at a
 * time, so the data model and downstream scoring are untouched. QuestionCard
 * renders one of these per market segment.
 */
import React, { useMemo, useState } from 'react';
import { Icon, Input } from 'semantic-ui-react';
import { MultiselectAnswer, PreferenceLevel, QuestionOption } from './types';
import { COMMUNITY_SOLAR_STATES, GEO_REGIONS } from './regions';
import PreferenceButtons from './PreferenceButtons';
import ConfirmationModal from '~/modals/ConfirmationModal';

interface Props {
  /** The geography options (US states + territories) from the question config. */
  options: QuestionOption[];
  /** This segment's current answer map (stateCode → preference level). */
  value: MultiselectAnswer;
  disabled?: boolean;
  /**
   * When true, the editor is scoped to the Community Solar segment: it limits
   * its options to the active community-solar markets and relabels the bulk
   * control accordingly. Front-end only — the stored map is unaffected.
   */
  communitySolar?: boolean;
  onChange: (next: MultiselectAnswer) => void;
}

/** The three tiers, in chip-section display order, with their copy. */
const TIERS: { level: PreferenceLevel; heading: string }[] = [
  { level: 'yes', heading: 'Sweet spot' },
  { level: 'maybe', heading: 'Open to' },
  { level: 'no', heading: 'Excluded' },
];

/** Cycle order when a chip body is clicked: yes → maybe → no → yes. */
const NEXT_LEVEL: Record<PreferenceLevel, PreferenceLevel> = {
  yes: 'maybe',
  maybe: 'no',
  no: 'yes',
};

/** A new map with `codes` set to `level` (or removed when level is undefined). */
const applyLevelToCodes = (
  current: MultiselectAnswer,
  codes: string[],
  level: PreferenceLevel | undefined
): MultiselectAnswer => {
  const next = { ...current };
  for (const code of codes) {
    if (level === undefined) delete next[code];
    else next[code] = level;
  }
  return next;
};

/** The level shared by every code, or undefined if mixed / incomplete. */
const uniformLevelForCodes = (
  current: MultiselectAnswer,
  codes: string[]
): PreferenceLevel | undefined => {
  if (codes.length === 0) return undefined;
  const first = current[codes[0]];
  if (!first) return undefined;
  return codes.every((c) => current[c] === first) ? first : undefined;
};

/** Whether setting `codes` to `level` would change a value already set. */
const wouldOverwriteCodes = (
  current: MultiselectAnswer,
  codes: string[],
  level: PreferenceLevel | undefined
): boolean =>
  codes.some((c) => current[c] !== undefined && current[c] !== level);

/** A bulk apply held pending the overwrite-confirmation modal. */
interface PendingApply {
  codes: string[];
  level: PreferenceLevel | undefined;
  /** Human label for the modal copy (e.g. "all states", "the Midwest"). */
  scope: string;
}

const GeographySelector: React.FC<Props> = ({
  options,
  value,
  disabled,
  communitySolar,
  onChange,
}) => {
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<PendingApply | null>(null);

  const bulkLabel = communitySolar ? 'All community solar markets' : 'All locations';

  // The "market" set the bulk control and region rows act on. For Community
  // Solar that's the active community-solar markets; other segments use the
  // full option list. Buyers can still add states outside this set via the
  // type-ahead — those show as chips but aren't swept by "All …".
  const marketOptions = useMemo(() => {
    if (!communitySolar) return options;
    const allowed = new Set(COMMUNITY_SOLAR_STATES);
    return options.filter((o) => allowed.has(o.value));
  }, [options, communitySolar]);
  const marketCodes = useMemo(() => marketOptions.map((o) => o.value), [marketOptions]);

  // Code → label from the *full* option list, so any individually-added state
  // (outside the market set) still renders with its name.
  const labelByCode = useMemo(() => {
    const map: Record<string, string> = {};
    for (const o of options) map[o.value] = o.label;
    return map;
  }, [options]);

  // The codes the editor actually shows: the market set plus any state the
  // buyer has set a preference on (added via type-ahead), in config order.
  const displayedCodes = useMemo(() => {
    const inMarket = new Set(marketCodes);
    return options
      .map((o) => o.value)
      .filter((c) => inMarket.has(c) || value[c] !== undefined);
  }, [options, marketCodes, value]);

  // Regions limited to the market codes present in the config (defensive
  // against the option list and the region table drifting apart), preserving
  // each region's declared state order.
  const regions = useMemo(() => {
    const known = new Set(marketCodes);
    return GEO_REGIONS.map((r) => ({
      name: r.name,
      states: r.states.filter((c) => known.has(c)),
    })).filter((r) => r.states.length > 0);
  }, [marketCodes]);

  // Bucket the set states by tier, in config order so chips read in a stable,
  // familiar sequence.
  const byTier = useMemo(() => {
    const buckets: Record<PreferenceLevel, string[]> = { yes: [], maybe: [], no: [] };
    for (const code of displayedCodes) {
      const level = value[code];
      if (level) buckets[level].push(code);
    }
    return buckets;
  }, [displayedCodes, value]);

  const counts = {
    yes: byTier.yes.length,
    maybe: byTier.maybe.length,
    no: byTier.no.length,
  };
  const setCount = counts.yes + counts.maybe + counts.no;
  const includedCount = counts.yes + counts.maybe;
  const total = displayedCodes.length;

  // Apply a bulk level, gating behind the overwrite-confirmation modal when it
  // would clobber answers the buyer already set.
  const requestApply = (
    codes: string[],
    level: PreferenceLevel | undefined,
    scope: string
  ) => {
    if (codes.length === 0) return;
    if (wouldOverwriteCodes(value, codes, level)) {
      setPending({ codes, level, scope });
    } else {
      onChange(applyLevelToCodes(value, codes, level));
    }
  };

  const confirmPending = () => {
    if (pending) onChange(applyLevelToCodes(value, pending.codes, pending.level));
    setPending(null);
  };

  // Search results: states not already set, matched by name or code. Capped so
  // the dropdown stays compact.
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    // The type-ahead searches the full option list (not just the market set),
    // so buyers can add any state to a Community Solar section.
    return options
      .filter((o) => value[o.value] === undefined)
      .filter(
        (o) =>
          o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [search, options, value]);

  const noun = communitySolar ? 'community solar markets' : 'locations';
  const headline = (() => {
    if (setCount === 0) return `No ${noun} selected yet.`;
    if (counts.yes === total) return `All ${noun} are a sweet spot (Yes).`;
    if (includedCount === total) return `All ${noun} included.`;
    const parts = [`${includedCount} of ${total} included`];
    if (counts.no > 0) parts.push(`${counts.no} excluded`);
    const unset = total - setCount;
    if (unset > 0) parts.push(`${unset} no preference`);
    return parts.join(' · ');
  })();

  return (
    <div className="buyer-profile-geo">
      <div className="buyer-profile-geo-controls">
        <div className="buyer-profile-geo-bulk-row">
          <span className="buyer-profile-geo-bulk-label">{bulkLabel}</span>
          <PreferenceButtons
            value={uniformLevelForCodes(value, marketCodes)}
            disabled={disabled}
            onChange={(level) =>
              requestApply(marketCodes, level, bulkLabel.toLowerCase())
            }
          />
          {setCount > 0 && (
            <button
              type="button"
              className="buyer-profile-geo-clear"
              disabled={disabled}
              // Clear everything the editor shows, including states added
              // outside the market set via the type-ahead.
              onClick={() => requestApply(displayedCodes, undefined, bulkLabel.toLowerCase())}
            >
              Clear
            </button>
          )}
        </div>

        {/* Region shortcuts only make sense across the full US list; for the
            short, fixed Community Solar market list the "all" control + chips +
            type-ahead are enough. */}
        {!communitySolar && (
        <div className="buyer-profile-geo-regions">
          {regions.map((region) => {
            const uniform = uniformLevelForCodes(value, region.states);
            const includedInRegion = region.states.filter(
              (c) => value[c] === 'yes' || value[c] === 'maybe'
            ).length;
            return (
              <div className="buyer-profile-geo-region-row" key={region.name}>
                <span className="buyer-profile-geo-region-name">
                  {region.name}
                  <span className="buyer-profile-geo-region-count">
                    {includedInRegion}/{region.states.length}
                  </span>
                </span>
                <PreferenceButtons
                  value={uniform}
                  disabled={disabled}
                  onChange={(level) =>
                    requestApply(region.states, level, `the ${region.name}`)
                  }
                />
              </div>
            );
          })}
        </div>
        )}

        {!disabled && (
          <div className="buyer-profile-geo-add">
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Add a specific state or territory…"
              value={search}
              onChange={(_e, d) => setSearch(d.value)}
            />
            {searchResults.length > 0 && (
              <ul className="buyer-profile-geo-results">
                {searchResults.map((o) => (
                  <li key={o.value}>
                    <button
                      type="button"
                      className="buyer-profile-geo-result"
                      onClick={() => {
                        onChange(applyLevelToCodes(value, [o.value], 'yes'));
                        setSearch('');
                      }}
                    >
                      <Icon name="plus" />
                      {o.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="buyer-profile-geo-summary">
        <div className="buyer-profile-geo-headline">{headline}</div>
        {!disabled && setCount > 0 && (
          <p className="buyer-profile-geo-hint">
            Click a location to cycle its preference (Yes → Maybe → No); use the
            <Icon name="close" /> to remove it.
          </p>
        )}
        {TIERS.map(({ level, heading }) => {
          const codes = byTier[level];
          if (codes.length === 0) return null;
          return (
            <div
              className={`buyer-profile-geo-tier buyer-profile-geo-tier--${level}`}
              key={level}
            >
              <span className="buyer-profile-geo-tier-heading">
                {heading} ({codes.length})
              </span>
              <div className="buyer-profile-geo-chips">
                {codes.map((code) => (
                  <span
                    className={`buyer-profile-geo-chip buyer-profile-geo-chip--${level}`}
                    key={code}
                  >
                    <button
                      type="button"
                      className="buyer-profile-geo-chip-cycle"
                      disabled={disabled}
                      title="Click to change preference"
                      onClick={() =>
                        onChange(applyLevelToCodes(value, [code], NEXT_LEVEL[level]))
                      }
                    >
                      {labelByCode[code] ?? code}
                    </button>
                    {!disabled && (
                      <button
                        type="button"
                        className="buyer-profile-geo-chip-remove"
                        aria-label={`Remove ${labelByCode[code] ?? code}`}
                        onClick={() =>
                          onChange(applyLevelToCodes(value, [code], undefined))
                        }
                      >
                        <Icon name="close" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {pending && (
        <ConfirmationModal
          titleText="Overwrite existing answers?"
          contentElement={
            <p>
              Applying this to {pending.scope} will replace preferences
              you&apos;ve already set there. Do you want to continue?
            </p>
          }
          confirmText="Overwrite"
          status="idle"
          mutate={confirmPending}
          closeModal={() => setPending(null)}
        />
      )}
    </div>
  );
};

export default GeographySelector;
